import {
    Box,
    Button,
    Card,
    CardContent,
    Chip,
    Grid,
    Typography,
    alpha,
    useTheme,
} from '@mui/material';
import {
    Chat as ChatIcon,
    Comment as CommentIcon,
    Undo as WithdrawIcon,
} from '@mui/icons-material';
import CategoryIcon from '@/components/CategoryIcon';
import { useI18nContext } from '@/i18n/i18n-react';

interface CategoryRequestCardProps {
    request: {
        id: number;
        categoryName: string;
        categoryIcon: string;
        requestType: 'join' | 'leave';
        message?: string;
        adminNote?: string;
        status: 'pending' | 'denied' | 'accepted';
    };
    onWithdraw?: (id: number) => void;
    isWithdrawing?: boolean;
}

export const CategoryRequestCard = ({
    request,
    onWithdraw,
    isWithdrawing = false,
}: CategoryRequestCardProps) => {
    const { LL } = useI18nContext();
    const theme = useTheme();
    const isPending = request.status === 'pending';
    const isDenied = request.status === 'denied';

    const statusColor = isPending ? 'warning' : 'error';
    const colorMain = isPending ? theme.palette.warning.main : theme.palette.error.main;

    return (
        <Grid size={{ xs: 12, sm: 6 }}>
            <Card
                variant="outlined"
                sx={{
                    bgcolor: alpha(colorMain, 0.05),
                    borderColor: alpha(colorMain, 0.3),
                    transition: 'all 0.2s',
                    ...(isPending && {
                        '&:hover': {
                            borderColor: colorMain,
                            boxShadow: `0 0 0 1px ${colorMain}`,
                        },
                    }),
                }}
            >
                <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
                    {/* Header: Icon, Title, Chip, and optional Withdraw Button */}
                    <Box
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1,
                            mb: request.message || request.adminNote ? 1 : 0,
                        }}
                    >
                        {/* Icon */}
                        <Box
                            sx={{
                                fontSize: '1.5rem',
                                color: `${statusColor}.main`,
                                display: 'flex',
                                flexShrink: 0,
                            }}
                        >
                            <CategoryIcon category={request.categoryIcon} />
                        </Box>

                        {/* Title */}
                        <Typography
                            variant="body2"
                            sx={{
                                fontWeight: 600,
                                flex: 1,
                                minWidth: 0,
                            }}
                        >
                            {request.categoryName}
                        </Typography>

                        {/* Type Chip */}
                        <Chip
                            label={
                                request.requestType === 'join'
                                    ? LL.categoryRequests.join()
                                    : LL.categoryRequests.leave()
                            }
                            size="small"
                            color={request.requestType === 'join' ? 'success' : 'warning'}
                            variant="outlined"
                            sx={{ height: 20, fontSize: '0.7rem' }}
                        />

                        {/* Withdraw Button (only for pending) */}
                        {isPending && onWithdraw && (
                            <Button
                                size="small"
                                color="error"
                                variant="text"
                                onClick={() => onWithdraw(request.id)}
                                disabled={isWithdrawing}
                                sx={{
                                    minWidth: 'auto',
                                    p: 0.5,
                                }}
                            >
                                <WithdrawIcon sx={{ fontSize: '1.1rem' }} />
                            </Button>
                        )}
                    </Box>

                    {/* User Message */}
                    {request.message && (
                        <Box
                            sx={{
                                display: 'flex',
                                gap: 1,
                                alignItems: 'flex-start',
                                ml: '2rem',
                                mb: request.adminNote ? 0.75 : 0,
                            }}
                        >
                            <ChatIcon
                                sx={{
                                    fontSize: '0.9rem',
                                    color: 'text.secondary',
                                    mt: 0.2,
                                }}
                            />
                            <Typography
                                variant="caption"
                                sx={{
                                    color: 'text.secondary',
                                    flex: 1,
                                }}
                            >
                                {LL.categoryRequests.request()}: {request.message}
                            </Typography>
                        </Box>
                    )}

                    {/* Admin Note (only for denied) */}
                    {isDenied && request.adminNote && (
                        <Box
                            sx={{
                                display: 'flex',
                                gap: 1,
                                alignItems: 'flex-start',
                                ml: '2rem',
                            }}
                        >
                            <CommentIcon
                                sx={{
                                    fontSize: '0.9rem',
                                    color: 'info.main',
                                    mt: 0.2,
                                }}
                            />
                            <Typography
                                variant="caption"
                                sx={{
                                    color: 'info.main',
                                    flex: 1,
                                }}
                            >
                                {LL.categoryRequests.answer()}: {request.adminNote}
                            </Typography>
                        </Box>
                    )}
                </CardContent>
            </Card>
        </Grid>
    );
};
